// lib/favorites.ts
// The card's heart → the SHOP's own favourites list (bazaar: OpenCart's
// `wishlist.add` / `wishlist.remove`). Runs in the shopper's browser on the host
// page, like add-to-cart (lib/cart/cartActions.ts): we only call their functions.
//
// Since digital4u's 2026-10-12 fix the heart is a toggle (ui.favoritesRemoveFunction)
// and opens already filled from their ids URL (ui.favoritesIdsUrl). A client
// without them keeps the add-only heart that starts empty on a new page.

import { resolveFunctionPath } from './cart/cartActions';

// The shopper's favourites as we know them — shared by every card, so a heart
// stays right across grid/list switches, re-renders and new searches.
const favourited = new Set<string>();
const listeners = new Set<() => void>();
// Bumped on every change so useSyncExternalStore re-renders the cards.
let version = 0;

function changed() {
  version++;
  listeners.forEach((l) => l());
}

export function subscribeFavourites(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function favouritesVersion(): number {
  return version;
}

export function isFavourited(productId: string | number): boolean {
  return favourited.has(String(productId));
}

/**
 * Call the shop's function. true → it was called without throwing (the shop
 * shows its own confirmation on its page; we fill the heart). false → the
 * function isn't on this page (e.g. our own /demo page) or it threw: the heart
 * stays as it was, the same "quietly nothing" as add-to-cart there.
 */
export function addToShopFavourites(productId: string | number, functionPath: string): boolean {
  if (typeof window === 'undefined') return false;
  const fn = resolveFunctionPath(window, functionPath);
  if (!fn) {
    console.warn(`[favorites] function "${functionPath}" not found on this page`);
    return false;
  }
  try {
    fn(String(productId));
    favourited.add(String(productId));
    changed();
    return true;
  } catch (err) {
    console.warn('[favorites] the shop function threw:', err);
    return false;
  }
}

// How long after our call the shop's own message may still appear (it comes
// back from their AJAX request, so it isn't there yet when fn() returns).
const NOTICE_WATCH_MS = 4000;

/**
 * Remove the shop's own pop-up message (ui.favoritesHideShopNotice) that their
 * favourites function shows in response to OUR click. Watches the host
 * document for NOTICE_WATCH_MS only, so their messages at any other time stay.
 */
export function hideShopNoticeAfterCall(selector: string, root: Document | undefined = typeof document === 'undefined' ? undefined : document): void {
  if (!root?.body || typeof MutationObserver === 'undefined') return;
  const sweep = () => {
    try {
      root.querySelectorAll(selector).forEach((el) => el.remove());
    } catch {
      /* invalid selector: leave their page alone */
    }
  };
  const observer = new MutationObserver(sweep);
  observer.observe(root.body, { childList: true, subtree: true });
  setTimeout(() => observer.disconnect(), NOTICE_WATCH_MS);
}

/** The remove counterpart of addToShopFavourites: same true/false contract. */
export function removeFromShopFavourites(productId: string | number, functionPath: string): boolean {
  if (typeof window === 'undefined') return false;
  const fn = resolveFunctionPath(window, functionPath);
  if (!fn) {
    console.warn(`[favorites] function "${functionPath}" not found on this page`);
    return false;
  }
  try {
    fn(String(productId));
    favourited.delete(String(productId));
    changed();
    return true;
  } catch (err) {
    console.warn('[favorites] the shop function threw:', err);
    return false;
  }
}

// Refetched when cards mount again after this long, so hearts the shopper
// changed on the shop's own pages (no reload) show right on the next open.
const IDS_MAX_AGE_MS = 30_000;
let idsLoadedAt = 0;
let idsInFlight: Promise<void> | null = null;

/**
 * Fill the hearts from the shop's ids URL (same-origin, with the shopper's
 * cookies; bazaar: [686, 6604], a guest gets []). The answer REPLACES what we
 * knew. Fails quietly — wrong host (our own /demo page), not JSON, network —
 * leaving the hearts as they are.
 */
export function loadShopFavourites(url: string): Promise<void> {
  if (typeof window === 'undefined' || typeof fetch === 'undefined') return Promise.resolve();
  if (idsInFlight) return idsInFlight;
  if (Date.now() - idsLoadedAt < IDS_MAX_AGE_MS) return Promise.resolve();
  idsInFlight = fetch(url, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
    .then((res) => (res.ok ? res.json() : null))
    .then((ids: unknown) => {
      if (!Array.isArray(ids)) return;
      favourited.clear();
      for (const id of ids) {
        if (typeof id === 'number' || typeof id === 'string') favourited.add(String(id));
      }
      idsLoadedAt = Date.now();
      changed();
    })
    .catch((err) => {
      console.warn('[favorites] could not read the favourites list:', err);
    })
    .finally(() => {
      idsInFlight = null;
    });
  return idsInFlight;
}

/** Tests only: forget the shared state between cases. */
export function resetFavouritesForTests() {
  favourited.clear();
  idsLoadedAt = 0;
  idsInFlight = null;
  version = 0;
}

// The host page's login state doesn't change without a page load, so look once.
const loggedInCache = new Map<string, boolean>();

/**
 * Whether the shopper is logged in on the HOST page, judged by a selector that
 * only matches for logged-in shoppers (bazaar: its logout link). Runs in the
 * host document (the widget's Shadow DOM lives inside it). No DOM → false.
 */
export function isShopperLoggedIn(selector: string): boolean {
  if (typeof document === 'undefined') return false;
  const known = loggedInCache.get(selector);
  if (known !== undefined) return known;
  let found = false;
  try {
    found = !!document.querySelector(selector);
  } catch {
    found = false; // an invalid selector hides the heart rather than crashing the card
  }
  loggedInCache.set(selector, found);
  return found;
}
